// PasswordCriteria component — real-time 5-criteria check & strength indicator
import * as React from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PasswordCriteriaResult {
  hasLength: boolean
  hasLower: boolean
  hasUpper: boolean
  hasNumber: boolean
  hasSpecial: boolean
  passedCount: number
  isValid: boolean
  strengthLabel: string
  strengthColor: string
}

export function checkPasswordCriteria(password: string): PasswordCriteriaResult {
  const p = password || ''
  const hasLength = p.length >= 10
  const hasLower = /[a-z]/.test(p)
  const hasUpper = /[A-Z]/.test(p)
  const hasNumber = /\d/.test(p)
  const hasSpecial = /[^A-Za-z0-9]/.test(p)

  const passedCount = [hasLength, hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length
  const isValid = hasLength && hasLower && hasUpper && hasNumber && hasSpecial

  let strengthLabel = 'Chưa nhập'
  let strengthColor = 'bg-muted/40'

  if (p.length > 0) {
    if (passedCount <= 2) {
      strengthLabel = 'Yếu'
      strengthColor = 'bg-danger'
    } else if (passedCount === 3) {
      strengthLabel = 'Trung bình'
      strengthColor = 'bg-warn'
    } else if (passedCount === 4) {
      strengthLabel = 'Khá'
      strengthColor = 'bg-accent'
    } else {
      strengthLabel = 'Mạnh & An toàn'
      strengthColor = 'bg-ok'
    }
  }

  return {
    hasLength,
    hasLower,
    hasUpper,
    hasNumber,
    hasSpecial,
    passedCount,
    isValid,
    strengthLabel,
    strengthColor,
  }
}

interface PasswordCriteriaProps {
  password: string
  onValidChange?: (isValid: boolean) => void
  className?: string
  showMeter?: boolean
}

export function PasswordCriteria({
  password,
  onValidChange,
  className,
  showMeter = true,
}: PasswordCriteriaProps) {
  const result = React.useMemo(() => checkPasswordCriteria(password), [password])

  React.useEffect(() => {
    onValidChange?.(result.isValid)
  }, [result.isValid, onValidChange])

  const criteria = [
    { label: 'Tối thiểu 10 ký tự', valid: result.hasLength },
    { label: 'Ít nhất 1 chữ thường (a-z)', valid: result.hasLower },
    { label: 'Ít nhất 1 chữ hoa (A-Z)', valid: result.hasUpper },
    { label: 'Ít nhất 1 chữ số (0-9)', valid: result.hasNumber },
    { label: 'Ít nhất 1 ký tự đặc biệt (!@#$%...)', valid: result.hasSpecial },
  ]

  return (
    <div className={cn('space-y-3 pt-1 text-xs', className)}>
      {showMeter && password.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-xs">
            <span className="text-inkSoft">Độ bảo mật mật khẩu:</span>
            <span className={cn('font-medium', {
              'text-danger ': result.passedCount <= 2,
              'text-warn ': result.passedCount === 3,
              'text-accent ': result.passedCount === 4,
              'text-ok ': result.passedCount === 5,
            })}>
              {result.strengthLabel}
            </span>
          </div>
          <div className="grid grid-cols-5 gap-1.5 h-1.5">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  result.passedCount >= level
                    ? result.strengthColor
                    : 'bg-muted/30 dark:bg-muted/20',
                )}
              />
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-rule/60 bg-paper/60 p-3 space-y-2">
        <p className="font-medium text-inkSoft text-xs">
          Điều kiện mật khẩu:
        </p>
        <div className="space-y-1.5">
          {criteria.map((item, idx) => (
            <div
              key={idx}
              className={cn(
                'flex items-center gap-2 transition-colors duration-200 text-xs',
                item.valid ? 'text-ok ' : 'text-inkSoft',
              )}
            >
              <div
                className={cn(
                  'size-4 rounded-full flex items-center justify-center transition-colors',
                  item.valid
                    ? 'bg-ok/12 text-ok  '
                    : 'bg-muted/30 text-muted dark:bg-muted/20',
                )}
              >
                {item.valid ? (
                  <Check className="size-2.5 stroke-[3]" />
                ) : (
                  <div className="size-1 rounded-full bg-muted" />
                )}
              </div>
              <span className={cn(item.valid && 'font-medium')}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

